import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, XCircle, Clock, User, Loader2, Plus, Trash2, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatDateTime, formatTime, formatTimeRangeUhr } from '@/lib/utils';

const API_BASE = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/v1`;

/**
 * Magic-Link-Seite für Lehrkräfte (Phase F6).
 * URL: /teacher-slot/:token[?action=confirm|decline]
 *
 * Die Seite ist öffentlich (kein Login), der Token aus der Einladungsmail
 * autorisiert die Aktion. Zwei Hauptaktionen:
 *   - Slot bestätigen (1-Klick, direkter Status-Wechsel)
 *   - Slot ablehnen (mit optionaler Begründung)
 */
export default function TeacherSlotMagicLinkPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedAction = searchParams.get('action'); // confirm|decline

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {status, message}
  const [declineMode, setDeclineMode] = useState(preselectedAction === 'decline');
  const [declineReason, setDeclineReason] = useState('');
  const [proposeMode, setProposeMode] = useState(preselectedAction === 'propose');
  const [altSlots, setAltSlots] = useState([{ date: '', start_time: '', duration_minutes: 45 }]);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API_BASE}/public/registrations/teacher-slot/${encodeURIComponent(token)}`,
      );
      setInfo(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Link nicht gültig oder abgelaufen.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE}/public/registrations/teacher-slot/${encodeURIComponent(token)}/confirm`,
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bestätigung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE}/public/registrations/teacher-slot/${encodeURIComponent(token)}/decline`,
        { reason: declineReason.trim() || null },
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ablehnen fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const proposeAlternatives = async () => {
    const valid = altSlots.filter((s) => s.date && s.start_time);
    if (valid.length === 0) {
      toast.error('Bitte mindestens einen Alternativtermin angeben');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE}/public/registrations/teacher-slot/${encodeURIComponent(token)}/propose-alternatives`,
        { slots: valid.map((s) => ({
          date: s.date,
          start_time: s.start_time,
          duration_minutes: parseInt(s.duration_minutes, 10) || 45,
        })) },
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const updateAlt = (idx, field, value) => {
    setAltSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const addAlt = () => setAltSlots((prev) => [...prev, { date: '', start_time: '', duration_minutes: 45 }]);
  const removeAlt = (idx) => setAltSlots((prev) => prev.filter((_, i) => i !== idx));

  const loadFreeSlots = async () => {
    try {
      const r = await axios.get(
        `${API_BASE}/public/registrations/teacher-slot/${encodeURIComponent(token)}/my-free-slots`,
        { params: { duration_minutes: 45, horizon_days: 28, limit: 10 } },
      );
      const slots = r.data?.slots || [];
      if (slots.length === 0) {
        toast.error('Keine freien Slots in Ihrer Verfügbarkeitsmatrix gefunden');
        return;
      }
      setAltSlots(slots.map((s) => ({
        date: s.date,
        start_time: s.start_time,
        duration_minutes: 45,
      })));
      toast.success(`${slots.length} freie Slots aus Ihrer Verfügbarkeit geladen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Laden fehlgeschlagen');
    }
  };

  const fmtDate = (iso) => formatDate(iso) || iso;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/40 py-10 px-4">
      <div className="max-w-xl mx-auto" data-testid="teacher-slot-magic-page">
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 flex items-center justify-center gap-3 text-slate-500" data-testid="teacher-slot-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Lade Slot-Anfrage …
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl shadow-sm border border-rose-200 p-8" data-testid="teacher-slot-error">
            <div className="flex items-center gap-2 text-rose-700 mb-2">
              <XCircle className="h-5 w-5" /> <span className="font-semibold">Link ungültig</span>
            </div>
            <p className="text-sm text-slate-600">{error}</p>
          </div>
        )}

        {!loading && !error && result && (
          <div className={`bg-white rounded-2xl shadow-sm border p-8 ${
            result.status === 'confirmed' ? 'border-emerald-300' :
            result.status === 'proposed_alternatives' ? 'border-brand-red/30' :
            'border-amber-300'
          }`} data-testid="teacher-slot-result">
            <div className={`flex items-center gap-2 mb-3 ${
              result.status === 'confirmed' ? 'text-emerald-700' :
              result.status === 'proposed_alternatives' ? 'text-brand-red' :
              'text-amber-700'
            }`}>
              {result.status === 'confirmed'
                ? <CheckCircle2 className="h-6 w-6" />
                : result.status === 'proposed_alternatives'
                  ? <CalendarClock className="h-6 w-6" />
                  : <XCircle className="h-6 w-6" />}
              <span className="text-lg font-semibold">
                {result.status === 'confirmed' ? 'Slot bestätigt' :
                 result.status === 'proposed_alternatives' ? 'Alternativen an Eltern gesendet' :
                 'Slot abgelehnt'}
              </span>
            </div>
            <p className="text-slate-700">{result.message}</p>
          </div>
        )}

        {!loading && !error && !result && info && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wider text-brand-red font-medium mb-1">{info.tenant_name}</div>
              <h1 className="text-2xl font-semibold text-slate-900">Neue Slot-Anfrage</h1>
              <p className="text-sm text-slate-500 mt-1">
                Hallo {info.teacher_name}, die Verwaltung bittet um Rückmeldung zu diesem Wunschtermin:
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-5 space-y-3 mb-6" data-testid="teacher-slot-details">
              <DetailRow icon={User} label="Schüler:in" value={`${info.student_name} — ${info.instrument}`} />
              <DetailRow icon={Calendar} label="Termin"
                value={`${info.slot_weekday}, ${fmtDate(info.slot_date)}`} />
              <DetailRow icon={Clock} label="Uhrzeit"
                value={`${formatTimeRangeUhr(info.slot_start_time, info.slot_end_time)} · ${info.slot_duration_minutes} Min · wöchentlich`} />
              <p className="text-xs text-slate-500 pt-1">
                Ihre Antwort geht an die Verwaltung. Der Anmelder wird von dort informiert.
              </p>
            </div>

            {info.status === 'confirmed' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800" data-testid="teacher-slot-already-confirmed">
                ✅ Dieser Termin wurde bereits bestätigt am {formatDateTime(info.confirmed_at)}.
              </div>
            )}
            {info.status === 'declined' && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-800" data-testid="teacher-slot-already-declined">
                ❌ Dieser Termin wurde bereits abgelehnt.
                {info.decline_reason && <div className="mt-1 italic">„{info.decline_reason}"</div>}
              </div>
            )}
            {info.status === 'proposed_alternatives' && (
              <div className="bg-brand-red/5 border border-brand-red/20 rounded-lg p-4 text-sm text-brand-red" data-testid="teacher-slot-already-proposed">
                📅 Alternativtermine sind bei der Verwaltung. Die Verwaltung leitet sie an den Anmelder weiter.
              </div>
            )}

            {info.status === 'pending' && !declineMode && !proposeMode && (
              <div className="flex flex-col sm:flex-row gap-3" data-testid="teacher-slot-actions">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
                  onClick={confirm}
                  disabled={submitting}
                  data-testid="teacher-slot-confirm-btn"
                >
                  {submitting
                    ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    : <CheckCircle2 className="h-5 w-5 mr-2" />}
                  Bestätigen
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base border-brand-red/30 text-brand-red hover:bg-brand-red/5"
                  onClick={() => setProposeMode(true)}
                  disabled={submitting}
                  data-testid="teacher-slot-propose-open-btn"
                >
                  <CalendarClock className="h-5 w-5 mr-2" /> Alternativen vorschlagen
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  onClick={() => setDeclineMode(true)}
                  disabled={submitting}
                  data-testid="teacher-slot-decline-open-btn"
                >
                  <XCircle className="h-5 w-5 mr-2" /> Ablehnen
                </Button>
              </div>
            )}

            {info.status === 'pending' && proposeMode && (
              <div className="space-y-3" data-testid="teacher-slot-propose-form">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-700">Alternativ-Termine vorschlagen (max. 10)</div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadFreeSlots} data-testid="teacher-slot-load-availability">
                      <CalendarClock className="h-4 w-4 mr-1" /> Aus meiner Verfügbarkeit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={addAlt} disabled={altSlots.length >= 10} data-testid="teacher-slot-alt-add">
                      <Plus className="h-4 w-4 mr-1" /> Manuell
                    </Button>
                  </div>
                </div>
                {altSlots.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg" data-testid={`teacher-slot-alt-row-${idx}`}>
                    <DateInput
                      
                      value={s.date}
                      onChange={(e) => updateAlt(idx, 'date', e.target.value)}
                      className="flex-1 h-10"
                      data-testid={`teacher-slot-alt-date-${idx}`}
                    />
                    <Input
                      type="time"
                      value={formatTime(s.start_time) || ''}
                      onChange={(e) => updateAlt(idx, 'start_time', e.target.value)}
                      className="w-32 h-10"
                      data-testid={`teacher-slot-alt-time-${idx}`}
                    />
                    <Input
                      type="number"
                      min={10} max={240} step={5}
                      value={s.duration_minutes}
                      onChange={(e) => updateAlt(idx, 'duration_minutes', e.target.value)}
                      className="w-20 h-10"
                      title="Dauer in Minuten"
                      data-testid={`teacher-slot-alt-duration-${idx}`}
                    />
                    {altSlots.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeAlt(idx)} className="h-10 w-10" data-testid={`teacher-slot-alt-remove-${idx}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => { setProposeMode(false); setAltSlots([{ date: '', start_time: '', duration_minutes: 45 }]); }}
                    disabled={submitting}
                    data-testid="teacher-slot-propose-cancel"
                  >
                    Zurück
                  </Button>
                  <Button
                    className="flex-1 bg-brand-red hover:bg-brand-red/90"
                    onClick={proposeAlternatives}
                    disabled={submitting}
                    data-testid="teacher-slot-propose-submit"
                  >
                    {submitting
                      ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      : <CalendarClock className="h-5 w-5 mr-2" />}
                    An Eltern senden
                  </Button>
                </div>
              </div>
            )}

            {info.status === 'pending' && declineMode && (
              <div className="space-y-3" data-testid="teacher-slot-decline-form">
                <label className="text-sm font-medium text-slate-700">Grund (optional)</label>
                <Textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="z. B. Termin kollidiert mit Probe / zu kurzer Vorlauf ..."
                  maxLength={500}
                  rows={3}
                  data-testid="teacher-slot-decline-reason"
                />
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setDeclineMode(false); setDeclineReason(''); }}
                    disabled={submitting}
                    data-testid="teacher-slot-decline-cancel"
                  >
                    Zurück
                  </Button>
                  <Button
                    className="flex-1 bg-rose-600 hover:bg-rose-700"
                    onClick={decline}
                    disabled={submitting}
                    data-testid="teacher-slot-decline-submit"
                  >
                    {submitting
                      ? <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      : <XCircle className="h-5 w-5 mr-2" />}
                    Ablehnung absenden
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-brand-red mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-xs uppercase text-slate-500 font-medium">{label}</div>
        <div className="text-slate-900 font-medium">{value}</div>
      </div>
    </div>
  );
}
